import type { ApiClient } from '../client';
import type { AddFeeItemRequest, AddFeeItemResponse, ApiResponse, AutoFeeInvoiceRunApiResponse, BulkGenerateInvoicesRequest, BulkGenerateInvoicesResponse, CompletePaytmPaymentRequest, CompletePaytmPaymentResponse, CreateAdvancePaymentRequest, CreateAdvancePaymentResponse, CreateFeeCategoryRequest, CreateFeeCategoryResponse, CreateFeePlanRequest, CreateFeePlanResponse, CreateRazorpayOrderRequest, CreateRazorpayOrderResponse, DeleteFeeCategoryRequest, DeleteFeeCategoryResponse, DeleteFeeItemRequest, DeleteFeeItemResponse, FeeApiOperationRequest, FeeBillingScheduleResponse, FeeCategorySummary, FeePaymentGatewayConfigResponse, GenerateInvoiceRequest, GenerateInvoiceResponse, InitiatePaytmPaymentRequest, InitiatePaytmPaymentResponse, ListFeeCategoriesResponse, ListFeeItemsQuery, ListFeeItemsResponse, ListFeePlansResponse, ListInvoicesQuery, ListInvoicesResponse, ListPaymentHistoryQuery, ListPaymentHistoryResponse, ListPaymentsQuery, ListPaymentsResponse, PreviewInvoiceRequest, PreviewInvoiceResponse, RecordPaymentRequest, RunFeeAutoInvoiceRequest, SearchFeeStudentsQuery, SearchFeeStudentsResponse, StudentCreditResponse, UpdateFeeBillingScheduleRequest, UpdateFeeCategoryRequest, UpdateFeePlanRequest, VerifyRazorpayPaymentRequest, VerifyRazorpayPaymentResponse } from '../types';
import { appendPaginationQuery } from '../../../types/pagination';

export function bindFees(client: ApiClient) {
  return {
async callFeeApi<T = unknown>(operation: FeeApiOperationRequest): Promise<ApiResponse<T>> {
    const qs = new URLSearchParams();
    Object.entries(operation.query || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      qs.append(key, String(value));
    });
    const query = qs.toString();
    const endpoint = query ? `${operation.path}?${query}` : operation.path;
    const options: RequestInit = { method: operation.method };
    if (operation.method === 'POST' && operation.body) {
      options.body = JSON.stringify(operation.body);
    }
    return client.request<T>(endpoint, options);
  },

async searchFeeStudents(params: SearchFeeStudentsQuery): Promise<ApiResponse<SearchFeeStudentsResponse>> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      qs.append(key, String(value));
    });
    const query = qs.toString();
    return client.request<SearchFeeStudentsResponse>(
      query ? `/fees/students/search?${query}` : '/fees/students/search'
    );
  },

async listFeeCategories(params?: { student_type?: string; student_id?: string }) {
    const qs = new URLSearchParams();
    if (params?.student_type) qs.append('student_type', params.student_type);
    if (params?.student_id) qs.append('student_id', params.student_id);
    const query = qs.toString();
    return client.request<ListFeeCategoriesResponse>(
      query ? `/fees/categories?${query}` : '/fees/categories'
    );
  },

async createFeeCategory(body: CreateFeeCategoryRequest) {
    return client.request<CreateFeeCategoryResponse>('/fees/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async updateFeeCategory(categoryId: string, body: CreateFeeCategoryRequest) {
    return client.request<FeeCategorySummary>('/fees/categories/update', {
      method: 'POST',
      body: JSON.stringify({ category_id: categoryId, ...body } satisfies UpdateFeeCategoryRequest),
    });
  },

async deleteFeeCategory(body: DeleteFeeCategoryRequest) {
    return client.request<DeleteFeeCategoryResponse>('/fees/categories/delete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listFeePlans() {
    return client.request<ListFeePlansResponse>('/fees/plans');
  },

async createFeePlan(body: CreateFeePlanRequest) {
    return client.request<CreateFeePlanResponse>('/fees/plans', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async updateFeePlan(planId: string, body: UpdateFeePlanRequest) {
    return client.request<void>(`/fees/plans/${encodeURIComponent(planId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

async listFeeItems(params?: ListFeeItemsQuery) {
    const qs = new URLSearchParams();
    if (params?.plan_id) qs.append('plan_id', params.plan_id);
    if (params?.template_id) qs.append('template_id', params.template_id);
    if (params?.category_id) qs.append('category_id', params.category_id);
    if (params?.student_id) qs.append('student_id', params.student_id);
    if (params?.student_type) qs.append('student_type', params.student_type);
    const query = qs.toString();
    return client.request<ListFeeItemsResponse>(query ? `/fees/items?${query}` : '/fees/items');
  },

async addFeeItem(body: AddFeeItemRequest) {
    return client.request<AddFeeItemResponse>('/fees/items', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async deleteFeeItem(body: DeleteFeeItemRequest) {
    return client.request<DeleteFeeItemResponse>('/fees/items/delete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listInvoices(params?: ListInvoicesQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    if (params?.student_id) qs.append('student_id', params.student_id);
    if (params?.status) qs.append('status', params.status);
    if (params?.billing_month?.trim()) qs.append('billing_month', params.billing_month.trim());
    const query = qs.toString();
    return client.request<ListInvoicesResponse>(query ? `/fees/invoices?${query}` : '/fees/invoices');
  },

async previewInvoice(body: PreviewInvoiceRequest) {
    return client.request<PreviewInvoiceResponse>('/fees/invoices/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async generateInvoice(body: GenerateInvoiceRequest) {
    return client.request<GenerateInvoiceResponse>('/fees/invoices/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async bulkGenerateInvoices(body: BulkGenerateInvoicesRequest) {
    return client.request<BulkGenerateInvoicesResponse>('/fees/invoices/generate-bulk', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async getFeeBillingSchedule() {
    return client.request<FeeBillingScheduleResponse>('/fees/billing-schedule');
  },

async updateFeeBillingSchedule(body: UpdateFeeBillingScheduleRequest) {
    return client.request<FeeBillingScheduleResponse>('/fees/billing-schedule', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

async runFeeAutoInvoiceGeneration(body?: RunFeeAutoInvoiceRequest) {
    return client.request<AutoFeeInvoiceRunApiResponse>('/fees/billing-schedule/run-now', {
      method: 'POST',
      body: JSON.stringify(body ?? { force: true }),
    });
  },

async listPayments(params?: ListPaymentsQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    if (params?.student_id) qs.append('student_id', params.student_id);
    const query = qs.toString();
    return client.request<ListPaymentsResponse>(query ? `/fees/payments?${query}` : '/fees/payments');
  },

async recordPayment(body: RecordPaymentRequest) {
    return client.request<void>('/fees/payments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async recordAdvancePayment(body: CreateAdvancePaymentRequest) {
    return client.request<CreateAdvancePaymentResponse>('/fees/advance-payments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async getStudentCredit(studentId: string) {
    return client.request<StudentCreditResponse>(
      `/fees/credits?student_id=${encodeURIComponent(studentId)}`
    );
  },

async listPaymentHistory(params: ListPaymentHistoryQuery) {
    const qs = new URLSearchParams();
    qs.append('student_id', params.student_id);
    if (params.transaction_type) qs.append('transaction_type', params.transaction_type);
    if (params.from_date) qs.append('from_date', params.from_date);
    if (params.to_date) qs.append('to_date', params.to_date);
    if (params.limit != null) qs.append('limit', String(params.limit));
    if (params.offset != null) qs.append('offset', String(params.offset));
    return client.request<ListPaymentHistoryResponse>(`/fees/payment-history?${qs.toString()}`);
  },

async getFeePaymentGatewayConfig() {
    return client.request<FeePaymentGatewayConfigResponse>('/fees/payments/gateway');
  },

async initiatePaytmPayment(body: InitiatePaytmPaymentRequest) {
    return client.request<InitiatePaytmPaymentResponse>('/fees/payments/paytm/initiate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async completePaytmPayment(body: CompletePaytmPaymentRequest) {
    return client.request<CompletePaytmPaymentResponse>('/fees/payments/paytm/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async createRazorpayOrder(body: CreateRazorpayOrderRequest) {
    return client.request<CreateRazorpayOrderResponse>('/fees/payments/razorpay/order', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async verifyRazorpayPayment(body: VerifyRazorpayPaymentRequest) {
    return client.request<VerifyRazorpayPaymentResponse>('/fees/payments/razorpay/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  };
}
